import { useCallback } from "react";
import { AppointmentDto, WorkingHoursDto } from "../api-client";
import useStore from "../helpers/useStore";
import { CalendarContext } from "../stores/Calendar/calendar.provider";
import {
  useAppointmentsForDate,
  useDerivedWorkingHours,
  useUsersByLocation,
} from "../stores/Calendar/calendar.queries";
import { useLocationId } from "../stores/User/User.hooks";
import { MutationPlan, MutationRequest, ShiftMutation } from "../stores/MutationDraft/types";
import { slotTherapy } from "../helpers/slotTherapy";

// ─── Helpers (pure functions, no React) ──────────────────────────────────────

/** Default slot duration when we can't derive it from existing appointments. */
const DEFAULT_DURATION_MIN = 20;

interface TimeRange {
  start: number; // epoch ms
  end: number;
}

/**
 * Build sorted occupied intervals for a specific therapist on the given date
 * from the existing appointments.
 */
const getOccupiedSlots = (therapistId: string, appointments: AppointmentDto[]): TimeRange[] => {
  const slots: TimeRange[] = [];
  for (const appt of appointments) {
    for (const ts of appt.timeSlots || []) {
      if (ts.user?.id === therapistId && ts.start && ts.end) {
        slots.push({
          start: new Date(ts.start).getTime(),
          end: new Date(ts.end).getTime(),
        });
      }
    }
  }
  return slots.sort((a, b) => a.start - b.start);
};

/**
 * Get working-hour blocks (non-break) for a therapist.
 */
const getWorkingBlocks = (therapistId: string, workingHours: WorkingHoursDto[]): TimeRange[] =>
  workingHours
    .filter((wh) => wh.userId === therapistId && !wh.isBreak)
    .map((wh) => ({
      start: new Date(wh.start).getTime(),
      end: new Date(wh.end).getTime(),
    }))
    .sort((a, b) => a.start - b.start);

/**
 * Find all gaps of at least `durationMs` within working blocks,
 * subtracting occupied intervals.
 */
const findGaps = (
  workingBlocks: TimeRange[],
  occupied: TimeRange[],
  durationMs: number,
): TimeRange[] => {
  const gaps: TimeRange[] = [];

  for (const block of workingBlocks) {
    const relevant = occupied.filter((o) => o.end > block.start && o.start < block.end);

    let cursor = block.start;
    for (const occ of relevant) {
      const gapEnd = Math.min(occ.start, block.end);
      if (gapEnd - cursor >= durationMs) {
        gaps.push({ start: cursor, end: gapEnd });
      }
      cursor = Math.max(cursor, occ.end);
    }
    if (block.end - cursor >= durationMs) {
      gaps.push({ start: cursor, end: block.end });
    }
  }

  return gaps;
};

/** Pick a random element from an array. */
const pickRandom = <T>(arr: T[]): T | undefined =>
  arr.length > 0 ? arr[Math.floor(Math.random() * arr.length)] : undefined;

/** Shuffle an array in place (Fisher-Yates). */
const shuffle = <T>(arr: T[]): T[] => {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

// ─── Enriched slot (carries IDs + label for ShiftMutation output) ────────────

interface OccupiedSlot extends TimeRange {
  appointmentId: string;
  timeSlotId: string;
  label: string;
}

const getTherapistSlots = (therapistId: string, appointments: AppointmentDto[]): OccupiedSlot[] => {
  const slots: OccupiedSlot[] = [];
  for (const appt of appointments) {
    for (const ts of appt.timeSlots || []) {
      if (ts.user?.id === therapistId && ts.start && ts.end) {
        const patient = ts.therapyRx?.rx?.patient as any;
        const therapy = (slotTherapy(ts) as any)?.abbreviation || "";
        const patientName = patient?.lastName || "";
        slots.push({
          start: new Date(ts.start).getTime(),
          end: new Date(ts.end).getTime(),
          appointmentId: appt.id || ts.appointmentId || "",
          timeSlotId: ts.id || "",
          label: [patientName, therapy].filter(Boolean).join(" · ") || "Termin",
        });
      }
    }
  }
  return slots.sort((a, b) => a.start - b.start);
};

/**
 * Try to insert a new appointment at insertion index by pushing subsequent
 * appointments later (right-push). Returns null if shifted appointments
 * would exceed the working block boundary.
 */
const tryInsertWithRightPush = (
  block: TimeRange,
  slotsInBlock: OccupiedSlot[],
  insertIdx: number,
  durationMs: number,
): {
  shifts: Array<{ slot: OccupiedSlot; newStart: number; newEnd: number }>;
  placementStart: number;
} | null => {
  const placementStart = insertIdx > 0 ? slotsInBlock[insertIdx - 1].end : block.start;
  const placementEnd = placementStart + durationMs;

  const shifts: Array<{ slot: OccupiedSlot; newStart: number; newEnd: number }> = [];
  let cursor = placementEnd;

  for (let i = insertIdx; i < slotsInBlock.length; i++) {
    const s = slotsInBlock[i];
    if (cursor > s.start) {
      const dur = s.end - s.start;
      shifts.push({ slot: s, newStart: cursor, newEnd: cursor + dur });
      cursor += dur;
    } else {
      break;
    }
  }

  if (cursor > block.end) return null;

  return { shifts, placementStart };
};

// ─── Hook ────────────────────────────────────────────────────────────────────

export interface UseCalendarMutationGeneratorReturn {
  /**
   * Given a MutationRequest, find a random gap and return a MutationPlan (no shifts).
   * Returns null if no gap is available.
   */
  generatePlacement: (request: MutationRequest) => MutationPlan | null;

  /**
   * Given a MutationRequest, try to fit it by shifting existing appointments.
   * Returns a MutationPlan or null if no arrangement works.
   */
  shiftExistingAndPlace: (request: MutationRequest) => MutationPlan | null;

  /** Whether the generator has enough data to compute (therapists, working hours loaded). */
  isReady: boolean;
}

/**
 * Generic calendar mutation generator.
 *
 * Accepts a `MutationRequest` (source-agnostic) and computes where to place
 * an appointment on the calendar — either in an existing gap or by shifting
 * other appointments.
 *
 * When the backend provides mutations directly, this hook can be bypassed.
 */
export const useCalendarMutationGenerator = (): UseCalendarMutationGeneratorReturn => {
  const CalendarStore = useStore(CalendarContext);
  const { selectedDateStr } = CalendarStore;
  const locationId = useLocationId();

  // Fetch therapists for this location
  const { data: allUsers = [] } = useUsersByLocation(locationId!, undefined, false, !!locationId);
  const therapistIds = allUsers
    .filter((u) => u.roles?.some((r: any) => r.id === "therapist"))
    .map((u) => u.id!)
    .filter(Boolean);

  // Fetch appointments on the selected date
  const selectedDate = new Date(selectedDateStr || new Date().toISOString().slice(0, 10));
  const { data: appointmentData } = useAppointmentsForDate(
    selectedDate,
    locationId!,
    false,
    { userIds: therapistIds },
    false,
    !!locationId && therapistIds.length > 0,
  );

  // Fetch working hours for all therapists on the selected date
  const { data: workingHours = [] } = useDerivedWorkingHours(
    therapistIds.length > 0 ? therapistIds : undefined,
    selectedDateStr || "",
    undefined,
    therapistIds.length > 0 && !!selectedDateStr,
    locationId ?? undefined,
  );

  const existingAppointments = appointmentData?.appointments ?? [];

  const isReady =
    !!locationId && therapistIds.length > 0 && !!selectedDateStr && workingHours.length > 0;

  /**
   * Given a MutationRequest, scan therapist schedules and return
   * a MutationPlan with no shifts, or null if no gap is available.
   */
  const generatePlacement = useCallback(
    (request: MutationRequest): MutationPlan | null => {
      if (!isReady) return null;

      const durationMs = request.durationMinutes * 60_000;

      // If preferred therapist is specified, try them first
      const orderedTherapists = request.preferredTherapistId
        ? [
            request.preferredTherapistId,
            ...therapistIds.filter((t) => t !== request.preferredTherapistId),
          ]
        : therapistIds;

      type Candidate = { therapistId: string; gap: TimeRange };
      const candidates: Candidate[] = [];

      for (const tid of orderedTherapists) {
        const blocks = getWorkingBlocks(tid, workingHours);
        if (blocks.length === 0) continue;
        const occupied = getOccupiedSlots(tid, existingAppointments);
        const gaps = findGaps(blocks, occupied, durationMs);
        for (const gap of gaps) {
          candidates.push({ therapistId: tid, gap });
        }
      }

      if (candidates.length === 0) return null;

      const pick = pickRandom(candidates)!;
      const user = allUsers.find((u) => u.id === pick.therapistId);
      const therapistName = user
        ? `${user.firstName || ""} ${user.lastName || ""}`.trim()
        : pick.therapistId;

      const startMs = pick.gap.start;
      const endMs = startMs + durationMs;

      return {
        shifts: [],
        placements: [
          {
            start: new Date(startMs).toISOString(),
            end: new Date(endMs).toISOString(),
            therapistId: pick.therapistId,
            therapistName,
            durationMinutes: request.durationMinutes,
          },
        ],
      };
    },
    [isReady, therapistIds, existingAppointments, workingHours, allUsers],
  );

  /**
   * Try to squeeze an appointment into a therapist's schedule by
   * right-pushing existing appointments.
   */
  const shiftExistingAndPlace = useCallback(
    (request: MutationRequest): MutationPlan | null => {
      if (!isReady) return null;

      const durationMs = request.durationMinutes * 60_000;

      // Randomise therapist order; prefer specified therapist
      const shuffledTherapists = request.preferredTherapistId
        ? [
            request.preferredTherapistId,
            ...shuffle([...therapistIds.filter((t) => t !== request.preferredTherapistId)]),
          ]
        : shuffle([...therapistIds]);

      for (const tid of shuffledTherapists) {
        const blocks = getWorkingBlocks(tid, workingHours);
        if (blocks.length === 0) continue;

        const allSlots = getTherapistSlots(tid, existingAppointments);

        for (const block of blocks) {
          const slotsInBlock = allSlots.filter((s) => s.start >= block.start && s.end <= block.end);

          const indices = shuffle(Array.from({ length: slotsInBlock.length + 1 }, (_, i) => i));

          for (const idx of indices) {
            const result = tryInsertWithRightPush(block, slotsInBlock, idx, durationMs);
            if (!result) continue;

            const user = allUsers.find((u) => u.id === tid);
            const therapistName = user
              ? `${user.firstName || ""} ${user.lastName || ""}`.trim()
              : tid;

            return {
              shifts: result.shifts.map(
                (sh): ShiftMutation => ({
                  appointmentId: sh.slot.appointmentId,
                  timeSlotId: sh.slot.timeSlotId,
                  label: sh.slot.label,
                  originalStart: new Date(sh.slot.start).toISOString(),
                  originalEnd: new Date(sh.slot.end).toISOString(),
                  originalTherapistId: tid,
                  originalTherapistName: therapistName,
                  newStart: new Date(sh.newStart).toISOString(),
                  newEnd: new Date(sh.newEnd).toISOString(),
                  newTherapistId: tid,
                  newTherapistName: therapistName,
                }),
              ),
              placements: [
                {
                  start: new Date(result.placementStart).toISOString(),
                  end: new Date(result.placementStart + durationMs).toISOString(),
                  durationMinutes: request.durationMinutes,
                  therapistId: tid,
                  therapistName,
                },
              ],
            };
          }
        }
      }

      return null;
    },
    [isReady, therapistIds, existingAppointments, workingHours, allUsers],
  );

  return { generatePlacement, shiftExistingAndPlace, isReady };
};
